import React, { useEffect, useRef } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import { trackAuthEvent } from '@/services/analytics';
import { getCompetencyTitle, IcfCompetencyCode } from '@/data/icfFormStatements';
import { CrfReadiness, DevelopmentPlanArtifact } from '@/types/icfProgress';
import { MentorSetupChecklistCard } from '@/components/mentorEngagements/MentorSetupChecklistCard';
import { MentorPlanWithChecklist } from '@/components/mentorEngagements/MentorPlanCard';
import { isMentorCraftPlan, isMentorWorkspacePlan } from '@/components/mentorEngagements/setupChecklist';

interface Props {
  plan?: DevelopmentPlanArtifact | null;
  /** When present, practice items are sourced from the CRF gap plan instead (WS-7.2). */
  crfReadiness?: CrfReadiness | null;
  locked?: boolean;
  testID?: string;
  /** Analytics surface of the mentor setup checklist that replaces a mentor workspace plan. */
  surface?: 'dashboard' | 'progress';
}

interface PracticeEntry {
  key: string;
  title: string;
  body: string;
  success_signal?: string;
}

export function WhatToWorkOnNextCard({ plan, crfReadiness, locked = false, testID, surface = 'progress' }: Props) {
  const { t, language } = useTranslation();
  const { isPhone } = useResponsive();
  const tracked = useRef(false);

  const gapPlan = crfReadiness?.gap_plan?.length ? crfReadiness.gap_plan : null;
  const mentorPlan = !gapPlan && isMentorCraftPlan(plan);
  const mentorChecklist = !gapPlan && !mentorPlan && isMentorWorkspacePlan(plan);

  useEffect(() => {
    if (tracked.current || mentorChecklist || mentorPlan) return;
    if (!plan && !gapPlan) return;
    tracked.current = true;
    trackAuthEvent('progress_plan_viewed', {
      source: gapPlan ? 'crf_gap_plan' : plan?.source,
      version: plan?.version,
    });
  }, [plan, gapPlan, mentorChecklist, mentorPlan]);

  // A mentor's plan card (their mentoring craft) sits next to the live setup checklist.
  if (mentorPlan && plan) {
    return <MentorPlanWithChecklist plan={plan} surface={surface} locked={locked} testID={testID || 'what_to_work_on_next_card'} />;
  }
  // F1/F2: the workspace fallback plan's steps are the checklist, so only the live checklist shows.
  if (mentorChecklist) {
    return <MentorSetupChecklistCard surface={surface} mentorPlan testID={testID || 'what_to_work_on_next_card'} />;
  }
  if (!plan && !gapPlan) return null;

  const gapEntries: PracticeEntry[] = gapPlan && crfReadiness
    ? gapPlan.map((item) => ({
      key: item.competency_code,
      title: `${item.competency_code} · ${getCompetencyTitle(crfReadiness.level, item.competency_code as IcfCompetencyCode, language)}`,
      body: item.practice,
      success_signal: t('icfProgress.crfReadiness.gapSuccessSignal', {
        count: item.statements_short,
        statements: item.statement_ids.join(', '),
      }),
    }))
    : [];

  const planEntries: PracticeEntry[] = (plan?.practice_items || []).map((item, index) => ({
    key: `${item.competency || index}-${item.title}`,
    title: item.title,
    body: item.body,
    success_signal: item.success_signal,
  }));

  const allPracticeItems = gapEntries.length > 0 ? gapEntries : planEntries;
  const practiceItems = locked ? allPracticeItems.slice(0, 1) : allPracticeItems;
  const allFocusAreas = plan?.focus_areas || [];
  const focusAreas = locked ? allFocusAreas.slice(0, 1) : allFocusAreas;
  const subtitle = gapPlan && crfReadiness
    ? t('icfProgress.crfReadiness.planSubtitle', {
      count: crfReadiness.statements_short_total,
      level: crfReadiness.level.toUpperCase(),
    })
    : t('icfProgress.plan.subtitle');

  return (
    <View testID={testID || 'what_to_work_on_next_card'} style={styles.card}>
      <View style={[styles.header, isPhone && styles.headerPhone]}>
        <View style={styles.titleRow}>
          <View style={styles.icon}>
            <MaterialIcons name="track-changes" size={21} color={reviewDesk.accent} />
          </View>
          <View style={styles.headerCopy}>
            <Text selectable style={styles.title}>{t('icfProgress.plan.title')}</Text>
            <Text selectable style={styles.subtitle}>{subtitle}</Text>
          </View>
        </View>
        {!!plan && (
          <Text selectable style={styles.version}>{t('icfProgress.plan.version', { version: plan.version })}</Text>
        )}
      </View>

      {focusAreas.length > 0 && (
        <View style={styles.focusWrap}>
          {focusAreas.map((area, index) => (
            <View key={`${area.competency || index}-${area.title}`} style={styles.focusChip}>
              <Text selectable testID="plan_focus_title" style={styles.focusTitle}>{area.title}</Text>
              {!!area.why && <Text selectable style={styles.focusWhy}>{area.why}</Text>}
            </View>
          ))}
        </View>
      )}

      <View style={styles.practiceList}>
        {practiceItems.length === 0 ? (
          <Text selectable style={styles.empty}>{t('icfProgress.plan.empty')}</Text>
        ) : practiceItems.map((item, index) => (
          <View key={item.key} style={styles.practiceItem}>
            <View style={styles.number}><Text style={styles.numberText}>{index + 1}</Text></View>
            <View style={styles.itemCopy}>
              <Text selectable style={styles.itemTitle}>{item.title}</Text>
              <Text selectable style={styles.itemBody}>{item.body}</Text>
              {!!item.success_signal && (
                <Text selectable style={styles.success}>
                  {t('icfProgress.plan.successSignal')}: {item.success_signal}
                </Text>
              )}
            </View>
          </View>
        ))}
      </View>

      <View style={styles.footer}>
        {!!plan && (
          <Text selectable style={styles.updated}>
            {t('icfProgress.plan.updated', { date: new Date(plan.updated_at).toLocaleDateString(language) })}
          </Text>
        )}
        {locked && <Text selectable style={styles.locked}>{t('icfProgress.plan.lockedTeaser')}</Text>}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: { width: '100%', backgroundColor: reviewDesk.card, borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 18, padding: 18, gap: 16 },
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 },
  headerPhone: { flexDirection: 'column' },
  titleRow: { flex: 1, flexDirection: 'row', alignItems: 'flex-start', gap: 11 },
  icon: { width: 38, height: 38, borderRadius: 11, alignItems: 'center', justifyContent: 'center', backgroundColor: reviewDesk.accentSoft },
  headerCopy: { flex: 1, gap: 3 },
  title: { color: reviewDesk.ink, fontSize: 18, fontWeight: '800' },
  subtitle: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 18 },
  version: { color: reviewDesk.accent, fontSize: 12, fontWeight: '700', backgroundColor: reviewDesk.accentSoft, borderRadius: 999, paddingHorizontal: 9, paddingVertical: 5 },
  focusWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  focusChip: { minWidth: 160, flex: 1, backgroundColor: reviewDesk.cardMuted, borderRadius: 11, padding: 11, gap: 3 },
  focusTitle: { color: reviewDesk.ink, fontSize: 13, fontWeight: '700' },
  focusWhy: { color: reviewDesk.muted, fontSize: 12, lineHeight: 17 },
  practiceList: { gap: 13 },
  practiceItem: { flexDirection: 'row', alignItems: 'flex-start', gap: 11 },
  number: { width: 27, height: 27, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: reviewDesk.accentSoft },
  numberText: { color: reviewDesk.accent, fontWeight: '800', fontVariant: ['tabular-nums'] },
  itemCopy: { flex: 1, gap: 4 },
  itemTitle: { color: reviewDesk.ink, fontSize: 15, fontWeight: '700' },
  itemBody: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 19 },
  success: { color: reviewDesk.success, fontSize: 12, lineHeight: 18, fontWeight: '600' },
  empty: { color: reviewDesk.muted, lineHeight: 20 },
  footer: { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: reviewDesk.line, paddingTop: 10, gap: 5 },
  updated: { color: reviewDesk.muted, fontSize: 11 },
  locked: { color: reviewDesk.accent, fontSize: 12, lineHeight: 17, fontWeight: '700' },
});

export default WhatToWorkOnNextCard;
